import { useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { Event, EVENT_TYPES } from "@/shared/contexts/event.context";
import { useCreateTaskMutation } from "@/shared/services/api/graphql/generated";

interface TaskVariables {
  type: string;
  params?: Record<string, any>;
  authorizedKeys?: string[];
  refetchQueries?: string[];
}

export const useEventHandler = () => {
  const [startTask] = useCreateTaskMutation();
  const apolloClient = useApolloClient();

  const handleRRACustomEvent = (taskVariables: TaskVariables) => {
    const { refetchQueries, ...variables } = taskVariables;

    // Create properly typed variables object
    const taskVars: any = {
      type: variables.type,
      params: variables.params || {},
      ...(variables.authorizedKeys && {
        authorizedKeys: variables.authorizedKeys,
      }),
    };

    window.dispatchEvent(
      new CustomEvent("show-toast", {
        detail: {
          type: "progress",
          description: "Syncing data...",
          keepOpen: true,
        },
      })
    );

    startTask({
      variables: taskVars,
      onCompleted: () => {
        // Wait 2 seconds after task completion before refetching queries
        setTimeout(async () => {
          if (refetchQueries && refetchQueries.length > 0) {
            try {
              // Manually refetch each query
              await Promise.all(
                refetchQueries.map((queryName) =>
                  apolloClient.refetchQueries({
                    include: [queryName],
                  })
                )
              );
            } catch (error) {
              console.error("Error refetching queries:", error);
            }
          }

          // Close toast after refetching is complete
          window.dispatchEvent(new CustomEvent("close-toast"));
        }, 2000);
      },
    });
  };

  const handleProjectsListView = (
    params?: Record<string, any>,
    options?: Record<string, any>
  ) => {
    const hasRRASync = import.meta.env.VITE_HAS_RRA_SYNC === "true";
    if (hasRRASync) {
      handleRRACustomEvent({
        type: "SYNC_RRA_PROJECTS",
        params: {},
        authorizedKeys: ["edm_id"],
        refetchQueries: ["Projects"],
      });
    }
  };

  const handleProjectDetailsView = (
    params?: Record<string, any>,
    options?: Record<string, any>
  ) => {
    const hasRRASync = import.meta.env.VITE_HAS_RRA_SYNC === "true";
    if (hasRRASync) {
      handleRRACustomEvent({
        type: "SYNC_RRA_ASSIGNMENT",
        params: {
          assignment_id: params?.project_id,
        },
        refetchQueries: ["Project", "People"],
      });
    }
  };

  const handlePersonView = (
    params?: Record<string, any>,
    options?: Record<string, any>
  ) => {
    const hasRRASync = import.meta.env.VITE_HAS_RRA_SYNC === "true";
    if (hasRRASync) {
      handleRRACustomEvent({
        type: "SYNC_RRA_PERSON_SCORES",
        params: {
          edm_person_id: params?.person_id,
        },
        refetchQueries: ["Dimensions"],
      });
    }
  };

  useEffect(() => {
    const handleEvent = (event: CustomEvent<Event>) => {
      const { type, params, customOptions } = event.detail;

      switch (type) {
        case EVENT_TYPES.PROJECTS_LIST_VIEW:
          handleProjectsListView(params, customOptions);
          break;
        case EVENT_TYPES.PROJECT_DETAILS_VIEW:
          handleProjectDetailsView(params, customOptions);
          break;
        case EVENT_TYPES.PERSON_VIEW:
          handlePersonView(params, customOptions);
          break;
        default:
          console.warn(`Unhandled event type: ${type}`);
      }
    };

    window.addEventListener("app-event", handleEvent as EventListener);

    return () => {
      window.removeEventListener("app-event", handleEvent as EventListener);
    };
  }, []);
};
